import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Video } from "lucide-react";

interface RecordingConsentProps {
  isRecording: boolean;
  meetingTitle: string;
  hostName: string;
  onAccept: () => void;
  onDecline: () => void;
}

export const RecordingConsent = ({
  isRecording,
  meetingTitle,
  hostName,
  onAccept,
  onDecline,
}: RecordingConsentProps) => {
  const [showConsent, setShowConsent] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  useEffect(() => {
    if (isRecording && !hasAcknowledged) {
      setShowConsent(true);
    }
  }, [isRecording, hasAcknowledged]);

  const handleAccept = () => {
    setHasAcknowledged(true);
    setShowConsent(false);
    onAccept();
  };

  const handleDecline = () => {
    setShowConsent(false);
    onDecline();
  };

  return (
    <>
      {/* Consent Dialog */}
      <AlertDialog open={showConsent} onOpenChange={setShowConsent}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-full">
                <Video className="h-6 w-6 text-destructive" />
              </div>
              <AlertDialogTitle>Recording Consent Required</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-4">
              <p>
                <strong>{hostName}</strong> has started recording this meeting:
              </p>
              <p className="font-semibold text-foreground">"{meetingTitle}"</p>
              
              <div className="bg-muted p-3 rounded-lg space-y-2 text-sm">
                <p className="font-semibold text-foreground">By continuing, you consent to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Audio and video recording of your participation</li>
                  <li>Recording of your screen share (if applicable)</li>
                  <li>Recording of chat messages and interactions</li>
                  <li>Potential transcription of audio content</li>
                </ul>
              </div>

              <p className="text-xs">
                The recording will be stored by the meeting host and may be used for documentation, training, or compliance purposes. If you do not consent to being recorded, please click "Leave Meeting" to exit.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDecline}>
              Leave Meeting
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleAccept}>
              I Consent, Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Persistent Recording Indicator */}
      {isRecording && hasAcknowledged && (
        <Alert className="fixed top-4 right-4 w-auto z-50 border-destructive bg-destructive/10 animate-pulse">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-sm font-semibold text-destructive">
            Recording in Progress
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
